import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from 'urql';

import {
  type CompositionV2Filters,
  PAY_V2_QUERY,
  PAY_V2_TOPGEOS_QUERY,
  type PayV2Req,
  PositionsEntityKind,
} from '@revelio/data-access';

import { countryNameToIsoCode } from '../../CustomDash/components/Chart/Map/utils/country-codes';
import {
  isCountry,
  isRegion,
} from '../../CustomDash/components/Chart/Map/utils/location-type-utils';
import pppRawData from '../cost-of-living-ppp.json';
import type { UpliftMode } from '../IndividualPotentialPage';
import type { CompanyRicsIds } from './useCompanyRics';
import {
  buildCustomRoleFilter,
  type ResolvedProfileIds,
} from './useResolveProfileIds';
import {
  BUCKET_SIZE,
  buildDistributionFromHistogram,
  findPercentileValue,
} from './useSalaryDistribution';

const pppData = pppRawData.data as Record<string, number>;

export type GeoLocationType = 'country' | 'msa' | 'region';

/**
 * All available regions in the Revelio taxonomy, matching the selection list.
 * Each entry has the numeric ID and display names used in the region picker.
 */
export const REGIONS = [
  { id: 1, shortName: 'N America', longName: 'Northern America' },
  { id: 4, shortName: 'S America', longName: 'Southern America' },
  { id: 5, shortName: 'N Europe', longName: 'Northern Europe' },
  { id: 3, shortName: 'W Europe', longName: 'Western Europe' },
  { id: 9, shortName: 'S Europe', longName: 'Southern Europe' },
  { id: 10, shortName: 'E Europe', longName: 'Eastern Europe' },
  { id: 6, shortName: 'E Asia', longName: 'Eastern Asia' },
  { id: 2, shortName: 'S Asia', longName: 'Southern Asia' },
  { id: 7, shortName: 'S-E Asia', longName: 'South-Eastern Asia' },
  { id: 13, shortName: 'C&W Asia', longName: 'Central and Western Asia' },
  { id: 14, shortName: 'Arab Sts', longName: 'Arab States' },
  {
    id: 8,
    shortName: 'C America',
    longName: 'Central America and the Caribbean',
  },
  { id: 11, shortName: 'SubSaharAf', longName: 'Sub-Saharan Africa' },
  { id: 15, shortName: 'N Africa', longName: 'Northern Africa' },
  { id: 12, shortName: 'Pacific', longName: 'Pacific Islands' },
] as const;

export interface GeoUpliftEntry {
  geoId: number;
  geoName: string;
  geoLongName: string;
  geoType: GeoLocationType;
  isoCode: string | null;
  baseSalary: number;
  totalComp: number;
  upliftDollar: number;
  upliftPercent: number;
  colAdjustedSalary: number | null;
  colAdjustedUpliftDollar: number | null;
  colAdjustedUpliftPercent: number | null;
  pppFactor: number | null;
}

export interface UseGeoSalaryUpliftResult {
  geoUplifts: GeoUpliftEntry[];
  userPppFactor: number | null;
  isLoading: boolean;
  percentileProgress: number;
  error: string | null;
}

/**
 * Look up a PPP factor by matching the geo name against the PPP dataset.
 * Uses case-insensitive matching and tries both short and long names.
 */
export const lookupPppFactor = (geoName: string): number | null => {
  const normalized = geoName.toLowerCase().trim();
  if (pppData[normalized] !== undefined) return pppData[normalized];

  // Try matching by checking if the geo name contains a country name
  for (const [country, factor] of Object.entries(pppData)) {
    if (normalized.includes(country) || country.includes(normalized)) {
      return factor;
    }
  }

  return null;
};

/**
 * Classify a geo name into country, MSA, or region.
 * Uses the same detection logic as the existing map infrastructure.
 */
const classifyGeoType = (name: string): GeoLocationType => {
  const normalized = name.toLowerCase().trim();
  if (isRegion(normalized)) return 'region';
  if (isCountry(normalized)) return 'country';
  // Default: anything else is likely an MSA/metro area
  return 'msa';
};

/**
 * Try to resolve a geo name to an ISO 3166-1 country code.
 * Works directly for country names, and extracts country from MSA names
 * like "New York-Newark-Jersey City, NY-NJ-PA" by checking against known countries.
 */
const resolveIsoCode = (
  name: string,
  geoType: GeoLocationType
): string | null => {
  const normalized = name.toLowerCase().trim();

  // Direct country match
  const directMatch = countryNameToIsoCode[normalized];
  if (directMatch) return directMatch;

  // For MSAs, try to find a country suffix (e.g., ", United States")
  if (geoType === 'msa') {
    const commaIdx = normalized.lastIndexOf(',');
    if (commaIdx > 0) {
      const suffix = normalized.substring(commaIdx + 1).trim();
      const suffixMatch = countryNameToIsoCode[suffix];
      if (suffixMatch) return suffixMatch;
    }
  }

  // Fuzzy: check if any country name is contained in the geo name
  for (const [country, code] of Object.entries(countryNameToIsoCode)) {
    if (country.length >= 4 && normalized.includes(country)) {
      return code;
    }
  }

  return null;
};

/**
 * Get the last (most recent) non-null value from a time series array.
 */
const getLatestValue = (series: ReadonlyArray<number | null>): number => {
  for (let i = series.length - 1; i >= 0; i--) {
    const val = series[i];
    if (val !== null && val !== undefined && val > 0) return val;
  }
  return 0;
};

const GEO_TYPE_TO_ENTITY: Record<GeoLocationType, PositionsEntityKind> = {
  country: PositionsEntityKind.Country,
  msa: PositionsEntityKind.Msa,
  region: PositionsEntityKind.Region,
};

/** Max geos to enrich with histogram data in percentile mode */
const MAX_PERCENTILE_GEOS = 20;

interface GeoStub {
  geoId: number;
  geoName: string;
  geoLongName: string;
  geoType: 'country' | 'msa';
  isoCode: string | null;
  medianBaseSalary: number;
  medianTotalComp: number;
}

/**
 * Fetches top geographies for the user's role and computes salary uplift
 * (both nominal and COL-adjusted).
 *
 * The pay_v2 API requires **geo entities** as the primary dimension to
 * return `top_geos` breakdowns. We use:
 *
 * - **selectedRegionId = null** (In Country): User's country as entity →
 *   API returns MSAs/cities within that country as top_geos
 * - **selectedRegionId = <id>** (Region): Selected region as entity →
 *   API returns countries within that region as top_geos
 *
 * The role and company are passed as **filters** to scope the salary data.
 *
 * `lookOutside` controls company scope:
 * - false: adds company/industry filter
 * - true: no company filter (all companies)
 */
export const useGeoSalaryUplift = (
  resolvedIds: ResolvedProfileIds,
  rics: CompanyRicsIds,
  userSalary: number,
  lookOutside: boolean,
  /** null = in-country (MSAs/cities), number = region ID (countries) */
  selectedRegionId: number | null,
  upliftMode: UpliftMode = 'median',
  userPercentile: number | null = null
): UseGeoSalaryUpliftResult => {
  // Determine user's PPP factor from their geography
  const userPppFactor = useMemo((): number | null => {
    const countryGeo = resolvedIds.geoLevels.find((g) => g.type === 'country');
    const geoToCheck = countryGeo ?? resolvedIds.primaryGeo;
    if (!geoToCheck) return null;
    return lookupPppFactor(geoToCheck.name);
  }, [resolvedIds.geoLevels, resolvedIds.primaryGeo]);

  // ── Step 1: Fetch top geos ──
  //
  // Single entity query: either user's country or a selected region.
  // Role is always a filter. This avoids the 10-entity limit entirely.

  const primaryRoleLevel = useMemo(
    () =>
      resolvedIds.roleLevels.find((l) => l.type === 'role_k150') ??
      resolvedIds.roleLevels[0] ??
      null,
    [resolvedIds.roleLevels]
  );

  const queryVariables = useMemo(() => {
    if (!primaryRoleLevel) return undefined;

    // Build filters: role + seniority + optionally company/industry
    const filters: CompositionV2Filters = { raw: false };
    filters.custom_role = buildCustomRoleFilter(primaryRoleLevel);

    if (resolvedIds.seniorityId) {
      filters.seniority = [resolvedIds.seniorityId];
    }

    if (!lookOutside) {
      if (resolvedIds.companyRcid) {
        filters.company = [resolvedIds.companyRcid];
      } else if (rics.ricsK400) {
        filters.rics_k400 = [rics.ricsK400];
      }
    }

    // Single entity: country for in-country, region for region view
    let entity: { EntID: number; EntType: PositionsEntityKind };

    if (selectedRegionId === null) {
      // In-country mode: use user's country
      const countryGeo = resolvedIds.geoLevels.find(
        (g) => g.type === 'country'
      );
      if (!countryGeo) return undefined;
      entity = {
        EntID: countryGeo.id,
        EntType: PositionsEntityKind.Country,
      };
    } else {
      // Region mode: use selected region
      entity = {
        EntID: selectedRegionId,
        EntType: PositionsEntityKind.Region,
      };
    }

    const req: PayV2Req = {
      entities: [entity],
      filters,
      options: {
        required_top_geos_length: 100,
      },
    };

    return { req };
  }, [
    primaryRoleLevel,
    resolvedIds.seniorityId,
    resolvedIds.companyRcid,
    resolvedIds.geoLevels,
    rics.ricsK400,
    lookOutside,
    selectedRegionId,
  ]);

  const [{ data, fetching, error }] = useQuery({
    query: PAY_V2_TOPGEOS_QUERY,
    variables: queryVariables,
    pause: !queryVariables,
  });

  // If "within company" returned too few results, try with industry filter
  const fewResults =
    !lookOutside &&
    !fetching &&
    data &&
    (data.pay_v2?.entities?.[0]?.metrics?.top_geos?.length ?? 0) < 3;

  const fallbackVariables = useMemo(() => {
    if (!fewResults || !primaryRoleLevel || !rics.ricsK400) return undefined;

    const filters: CompositionV2Filters = { raw: false };
    filters.custom_role = buildCustomRoleFilter(primaryRoleLevel);
    if (resolvedIds.seniorityId) {
      filters.seniority = [resolvedIds.seniorityId];
    }
    filters.rics_k400 = [rics.ricsK400];

    let entity: { EntID: number; EntType: PositionsEntityKind };

    if (selectedRegionId === null) {
      const countryGeo = resolvedIds.geoLevels.find(
        (g) => g.type === 'country'
      );
      if (!countryGeo) return undefined;
      entity = { EntID: countryGeo.id, EntType: PositionsEntityKind.Country };
    } else {
      entity = { EntID: selectedRegionId, EntType: PositionsEntityKind.Region };
    }

    const req: PayV2Req = {
      entities: [entity],
      filters,
      options: {
        required_top_geos_length: 100,
      },
    };

    return { req };
  }, [
    fewResults,
    primaryRoleLevel,
    resolvedIds.seniorityId,
    resolvedIds.geoLevels,
    rics.ricsK400,
    selectedRegionId,
  ]);

  const [{ data: fallbackData, fetching: fallbackFetching }] = useQuery({
    query: PAY_V2_TOPGEOS_QUERY,
    variables: fallbackVariables,
    pause: !fallbackVariables,
  });

  const activeData = fewResults && fallbackData ? fallbackData : data;
  const topGeosLoading = fetching || (fewResults === true && fallbackFetching);

  // ── Parse top geos into stubs ──

  const geoStubs = useMemo((): GeoStub[] => {
    const topGeos = activeData?.pay_v2?.entities?.[0]?.metrics?.top_geos;
    if (!topGeos || topGeos.length === 0) return [];

    const stubs: GeoStub[] = [];
    for (const geo of topGeos) {
      const geoType = classifyGeoType(geo.longName);
      if (geoType === 'region') continue;
      const medianBaseSalary = getLatestValue(geo.base);
      if (medianBaseSalary <= 0) continue;

      stubs.push({
        geoId: geo.index,
        geoName: geo.shortName,
        geoLongName: geo.longName,
        geoType,
        isoCode: resolveIsoCode(geo.longName, geoType),
        medianBaseSalary,
        medianTotalComp: getLatestValue(geo.total),
      });
    }
    return stubs;
  }, [activeData]);

  // ── Step 2 (percentile mode only): Sequential per-geo histogram queries ──

  const geosToEnrich = useMemo(
    () => geoStubs.slice(0, MAX_PERCENTILE_GEOS),
    [geoStubs]
  );

  const [pctlIndex, setPctlIndex] = useState(0);
  const [pctlResults, setPctlResults] = useState<Map<number, number | null>>(
    new Map()
  );
  const prevPctlKeyRef = useRef<string>('');

  // Reset percentile enrichment when geos or mode change
  const geosKey = geosToEnrich.map((g) => g.geoId).join(',');
  const pctlCompositeKey = `${geosKey}|${upliftMode}|${userPercentile}|${lookOutside}|${selectedRegionId}`;
  useEffect(() => {
    if (pctlCompositeKey !== prevPctlKeyRef.current) {
      prevPctlKeyRef.current = pctlCompositeKey;
      setPctlIndex(0);
      setPctlResults(new Map());
    }
  }, [pctlCompositeKey]);

  const isPercentileMode =
    upliftMode === 'percentile' && userPercentile !== null;
  const needsPercentileEnrichment = isPercentileMode && geosToEnrich.length > 0;

  const currentGeo =
    needsPercentileEnrichment && pctlIndex < geosToEnrich.length
      ? geosToEnrich[pctlIndex]
      : undefined;

  const perGeoVariables = useMemo(() => {
    if (!currentGeo || !primaryRoleLevel) return undefined;

    const entityType = GEO_TYPE_TO_ENTITY[currentGeo.geoType];

    return {
      req: {
        entities: [
          {
            EntID: currentGeo.geoId,
            EntType: entityType,
          },
        ],
        filters: {
          raw: false,
          custom_role: buildCustomRoleFilter(primaryRoleLevel),
          ...(resolvedIds.seniorityId
            ? { seniority: [resolvedIds.seniorityId] }
            : {}),
        } as CompositionV2Filters,
      },
    };
  }, [currentGeo, primaryRoleLevel, resolvedIds.seniorityId]);

  const [{ data: perGeoData, fetching: perGeoFetching, error: perGeoError }] =
    useQuery({
      query: PAY_V2_QUERY,
      variables: perGeoVariables ?? {
        req: { entities: [], filters: { raw: false } },
      },
      pause: !perGeoVariables,
    });

  // When per-geo data arrives, extract percentile salary and advance
  useEffect(() => {
    if (!currentGeo || perGeoFetching || !needsPercentileEnrichment) return;
    if (pctlIndex >= geosToEnrich.length) return;

    if (perGeoData || perGeoError) {
      let percentileSalary: number | null = null;

      if (perGeoData && !perGeoError && userPercentile !== null) {
        const histogram = perGeoData.pay_v2?.entities?.[0]?.metrics?.histogram;
        const baseHistogram = histogram?.base;
        if (baseHistogram && baseHistogram.length > 0) {
          const distribution = buildDistributionFromHistogram(
            baseHistogram.map((c: number | null) => c ?? 0),
            BUCKET_SIZE
          );
          percentileSalary = findPercentileValue(
            distribution,
            userPercentile / 100
          );
        }
      }

      setPctlResults((prev) => {
        const next = new Map(prev);
        next.set(currentGeo.geoId, percentileSalary);
        return next;
      });
      setPctlIndex((prev) => prev + 1);
    }
  }, [
    perGeoData,
    perGeoError,
    perGeoFetching,
    currentGeo,
    pctlIndex,
    geosToEnrich.length,
    needsPercentileEnrichment,
    userPercentile,
  ]);

  const percentileEnrichmentDone =
    !needsPercentileEnrichment || pctlIndex >= geosToEnrich.length;

  const percentileProgress =
    geosToEnrich.length > 0 ? pctlIndex / geosToEnrich.length : 1;

  // ── Step 3: Assemble final geo uplifts ──

  const geoUplifts = useMemo((): GeoUpliftEntry[] => {
    return geoStubs
      .map((stub) => {
        let baseSalary = stub.medianBaseSalary;
        const totalComp = stub.medianTotalComp;

        if (isPercentileMode) {
          const pctlSalary = pctlResults.get(stub.geoId);
          if (pctlSalary !== null && pctlSalary !== undefined) {
            baseSalary = pctlSalary;
          }
        }

        const upliftDollar = baseSalary - userSalary;
        const upliftPercent =
          userSalary > 0 ? (upliftDollar / userSalary) * 100 : 0;

        // COL adjustment — use the country's PPP factor
        const geoPpp = lookupPppFactor(stub.geoLongName);
        let colAdjustedSalary: number | null = null;
        let colAdjustedUpliftDollar: number | null = null;
        let colAdjustedUpliftPercent: number | null = null;

        if (geoPpp !== null && userPppFactor !== null && geoPpp > 0) {
          colAdjustedSalary = baseSalary * (userPppFactor / geoPpp);
          colAdjustedUpliftDollar = colAdjustedSalary - userSalary;
          colAdjustedUpliftPercent =
            userSalary > 0 ? (colAdjustedUpliftDollar / userSalary) * 100 : 0;
        }

        return {
          geoId: stub.geoId,
          geoName: stub.geoName,
          geoLongName: stub.geoLongName,
          geoType: stub.geoType,
          isoCode: stub.isoCode,
          baseSalary,
          totalComp,
          upliftDollar,
          upliftPercent,
          colAdjustedSalary,
          colAdjustedUpliftDollar,
          colAdjustedUpliftPercent,
          pppFactor: geoPpp,
        };
      })
      .sort((a, b) => b.upliftPercent - a.upliftPercent);
  }, [geoStubs, userSalary, userPppFactor, isPercentileMode, pctlResults]);

  const isLoading =
    topGeosLoading || (needsPercentileEnrichment && !percentileEnrichmentDone);

  return {
    geoUplifts,
    userPppFactor,
    isLoading,
    percentileProgress,
    error: error ? 'Failed to load geographic salary data' : null,
  };
};
